/**
 * Table order item helper.
 * Centralizes transformation of items from different sources to cart format.
 */

import indexDbHelper from "./indexDbHelper";
import {
  calculateItemTotals,
  matchTaxRateByValue,
  getOrderItemProductId
} from "./tableOrderCartHelper";

/**
 * Transform items from IndexedDB format to cart items format
 * @param {Array} savedItems - Items from IndexedDB
 * @param {Function} filterFn - Optional filter function to apply to items before transformation
 * @returns {Array} - Transformed cart items
 */
export function transformIndexedDBItemsToCart(savedItems, filterFn = null) {
  if (!savedItems || !Array.isArray(savedItems) || savedItems.length === 0) {
    return [];
  }

  let itemsToTransform = savedItems;

  if (filterFn && typeof filterFn === "function") {
    itemsToTransform = savedItems.filter(filterFn);
  }

  return itemsToTransform.map((item) => {
    const product = item.product || {
      id: item.id,
      tax_rates: item.tax_rates || []
    };

    const selectedModifiers = item.selectedModifiers || item.modifiers || [];
    const {
      itemTotal,
      itemTotalWithoutDiscount,
      hasDiscount,
      modifiersText
    } = calculateItemTotals({
      price: item.price,
      modifiers: selectedModifiers,
      quantity: item.quantity || 1,
      discount: item.discount || 0,
      wholeDiscount: item.wholeDiscount || 0
    });

    return {
      ...item,
      cartId: item.cartId || item.local_id || `${item.id}-${Date.now()}`,
      local_id: item.local_id || item.cartId || `${item.id}-${Date.now()}`,
      selectedModifiers: selectedModifiers,
      product: product,
      tax_status:
        item.tax_status !== undefined
          ? item.tax_status
          : item.tax_rate_value
          ? 0
          : 1,
      canRemove: item.canRemove !== undefined ? item.canRemove : true,
      isSent: false, // Items from IndexedDB are always new items
      itemTotal: itemTotal,
      itemTotalWithoutDiscount: itemTotalWithoutDiscount,
      hasDiscount: hasDiscount,
      modifiersText: modifiersText
    };
  });
}

/**
 * Transform order items from API format to cart items format (sent items)
 * @param {Array} orderItems - Items from order API response
 * @param {Object} options - Transformation options
 * @param {Object} options.taxRates - Available tax rates from store
 * @param {Function} options.filterFn - Optional filter function to apply to items before transformation
 * @returns {Array} - Transformed cart items with isSent: true
 */
export function transformOrderItemsToCart(orderItems, options = {}) {
  if (!orderItems || !Array.isArray(orderItems) || orderItems.length === 0) {
    return [];
  }

  const {
    taxRates = [],
    filterFn = null,
    isStoreTaxInclusive = false
  } = options;
  let itemsToTransform = orderItems;

  if (filterFn && typeof filterFn === "function") {
    itemsToTransform = orderItems.filter(filterFn);
  }

  return itemsToTransform.map((orderItem) => {
    const cartId = `sent-${orderItem.id ||
      orderItem.product_id}-${orderItem.order_item_id || Date.now()}`;
    const localId = cartId;

    let itemTaxRates =
      orderItem.tax_rates ||
      (orderItem.product && orderItem.product.tax_rates
        ? orderItem.product.tax_rates
        : []);

    itemTaxRates = matchTaxRateByValue({
      itemTaxRates,
      taxRateValue: orderItem.tax_rate_value,
      availableTaxRates: taxRates
    });

    const product = {
      // Product id, never the OrderItem PK — cart reconciliation matches on it.
      id: getOrderItemProductId(orderItem),
      tax_rates: itemTaxRates
    };

    const selectedModifiers =
      orderItem.modifiers || orderItem.modifier_items || [];

    const taxTotal = orderItem.tax || 0;
    const taxPerUnit =
      orderItem.quantity > 0 ? taxTotal / orderItem.quantity : taxTotal;
    const taxItems = orderItem.tax_items || [];

    let itemTax = 0;
    if (taxItems && Array.isArray(taxItems) && taxItems.length > 0) {
      itemTax = taxItems.reduce((taxSum, taxItem) => {
        return taxSum + (taxItem.tax_total || 0);
      }, 0);
    } else if (taxTotal !== undefined && taxTotal !== null) {
      itemTax = taxTotal;
    } else if (taxPerUnit !== undefined && taxPerUnit !== null) {
      itemTax = taxPerUnit * (orderItem.quantity || 1);
    }

    const discount = orderItem.discount || 0;
    const wholeDiscount = orderItem.wholeDiscount || 0;

    // For sent items, use grand_total if available, otherwise calculate
    let itemTotal = 0;
    let itemTotalWithoutDiscount = 0;
    let hasDiscount = false;
    let modifiersText = null;

    if (orderItem.grand_total !== undefined && orderItem.grand_total !== null) {
      if (isStoreTaxInclusive) {
        itemTotal = orderItem.grand_total;
      } else {
        itemTotal = orderItem.grand_total - itemTax;
      }
      const subtotalWithDiscount = itemTotal;
      itemTotalWithoutDiscount =
        subtotalWithDiscount + discount + wholeDiscount;
      hasDiscount = discount + wholeDiscount > 0;
      modifiersText =
        selectedModifiers && selectedModifiers.length > 0
          ? selectedModifiers.map((m) => m.name).join(", ")
          : null;
    } else {
      const calculated = calculateItemTotals({
        price: orderItem.price,
        modifiers: selectedModifiers,
        quantity: orderItem.quantity || 1,
        discount,
        wholeDiscount
      });
      itemTotal = calculated.itemTotal;
      itemTotalWithoutDiscount = calculated.itemTotalWithoutDiscount;
      hasDiscount = calculated.hasDiscount;
      modifiersText = calculated.modifiersText;
    }

    return {
      // Cart item `id` is the PRODUCT id (matches catalog items added locally);
      // line identity lives in cartId/local_id. Falling back to orderItem.id
      // here (the OrderItem PK) used to poison every reconciliation match.
      id: getOrderItemProductId(orderItem),
      name: orderItem.name || orderItem.product?.name || "",
      price: orderItem.price || 0,
      quantity: orderItem.quantity || 1,
      note: orderItem.note || orderItem.notes || "",
      selectedModifiers: selectedModifiers,
      modifiers: selectedModifiers,
      product: product,
      tax_rate_value: orderItem.tax_rate_value || 0,
      tax_status:
        orderItem.tax_status !== undefined
          ? orderItem.tax_status
          : orderItem.tax_rate_value
          ? 0
          : 1,
      tax_rates: itemTaxRates,
      tax: taxPerUnit,
      tax_total: taxTotal,
      tax_items: taxItems,
      subtotal: orderItem.subtotal,
      grand_total: orderItem.grand_total,
      discount: discount,
      wholeDiscount: wholeDiscount,
      itemTotal: itemTotal,
      itemTotalWithoutDiscount: itemTotalWithoutDiscount,
      hasDiscount: hasDiscount,
      modifiersText: modifiersText,
      cartId: cartId,
      local_id: localId,
      image:
        orderItem.image ||
        orderItem.product?.image ||
        orderItem.image_url ||
        null,
      canRemove: false,
      isSent: true
    };
  });
}

/**
 * Load and transform items from IndexedDB for table orders
 * @param {Function} filterFn - Optional filter function to apply to items
 * @returns {Promise<Array>} - Transformed cart items from IndexedDB
 */
export async function loadItemsFromIndexedDB(filterFn = null) {
  try {
    const savedItems = await indexDbHelper.getDbData("tableOrderItems");

    if (!savedItems || !Array.isArray(savedItems) || savedItems.length === 0) {
      return [];
    }

    return transformIndexedDBItemsToCart(savedItems, filterFn);
  } catch (error) {
    console.error(
      "[tableOrderItemHelper] Error loading items from IndexedDB:",
      error
    );
    return [];
  }
}
